// ============================================
// CHECKOUTPAGE.JS - Página de pago (Stripe + Medusa)
// Ruta: /checkout
// Flujo: dirección → método de envío → Payment Element → completar orden
// ============================================

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useCart } from "../context/CartContext";
import { getStripe, isStripeConfigured } from "../services/stripe";
import {
  setCartDetails,
  listShippingOptions,
  addShippingMethod,
  initStripePaymentSession,
  completeCart,
} from "../services/medusa/checkout";
import SEO from "../components/SEO";
import StateSearchInput from "../components/checkout/StateSearchInput";
import { useDistributorProgress } from "../hooks/useDistributorProgress";
import { getShippingQuote, isQuoteOption } from "../services/medusa/shippingQuote";
import "./CheckoutPage.css";
import { formatAmount } from '../utils/money';

const stripePromise = getStripe();

const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="16" height="16">
    <path d="M19 12H5M5 12l7 7M5 12l7-7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

// Formatea un monto de Medusa a MXN (Medusa v2 guarda pesos, no centavos).
const money = (n) => `$${formatAmount(n)}`;

// Precio de una opción de envío (Medusa v2 puede exponerlo como `amount`
// o como `calculated_price.calculated_amount`). Devuelve el monto ya en MXN.
const shippingAmount = (opt) => {
  const raw = opt?.amount ?? opt?.calculated_price?.calculated_amount ?? 0;
  return raw;
};

// ----------------------------------------------------------------------------
// Formulario de pago: vive DENTRO de <Elements>, por eso puede usar los hooks
// useStripe / useElements y montar el Payment Element (tarjeta + OXXO).
// ----------------------------------------------------------------------------
function StripePaymentForm({ cartId, total, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const finalizeOrder = useCallback(async () => {
    const res = await completeCart(cartId);
    if (res.type === "order") {
      onSuccess(res.order);
    } else {
      // El banco ya autorizó: avisar que NO se cobró y que no reintente a
      // ciegas (cada intento deja otra retención en su tarjeta).
      throw new Error(
        `${res.error?.message || "No se pudo completar el pedido."} ` +
          "Tu tarjeta no fue cobrada (la retención se libera sola). Antes de " +
          "intentar de nuevo, escríbenos a hello@physiologyskincare.com."
      );
    }
  }, [cartId, onSuccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError(null);

    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // Métodos que exigen redirección (OXXO, 3D Secure) vuelven aquí.
          return_url: `${window.location.origin}/checkout?redirect=stripe`,
        },
        redirect: "if_required",
      });

      if (stripeError) {
        setError(stripeError.message || "El pago no pudo procesarse.");
        setSubmitting(false);
        return;
      }

      // Pago síncrono (tarjeta sin 3DS): confirmamos y creamos la orden.
      const okStatuses = ["succeeded", "processing", "requires_capture"];
      if (paymentIntent && okStatuses.includes(paymentIntent.status)) {
        await finalizeOrder();
        return; // onSuccess navega fuera; no reseteamos submitting.
      }

      setError("El pago quedó en un estado inesperado. Revisa tu método de pago.");
      setSubmitting(false);
    } catch (err) {
      setError(err.message || "Ocurrió un error al procesar el pago.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <section className="checkout-section">
        <h2 className="checkout-section-title">Método de pago</h2>
        {/* Lista con radios (no pestañas) y Link hasta abajo: varias clientas
            lo elegían creyendo que era "pagar directo con Stripe". */}
        <PaymentElement
          options={{
            layout: { type: "accordion", radios: "always", spacedAccordionItems: false, defaultCollapsed: false },
            paymentMethodOrder: ["card", "oxxo", "link"],
          }}
        />
        {error && <p className="checkout-error">{error}</p>}
      </section>

      <button type="submit" className="checkout-place-btn" disabled={!stripe || submitting}>
        {submitting ? (
          <span className="checkout-loading">Procesando…</span>
        ) : (
          <>
            <LockIcon />
            Confirmar pedido · {money(total)}
          </>
        )}
      </button>

      <p className="checkout-secure-note">
        <LockIcon /> Pago seguro procesado por Stripe
      </p>
    </form>
  );
}

// ----------------------------------------------------------------------------
export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, cartItems, subtotal, discount, clearCart, has2x1, ofertasAplicadas } = useCart();

  const cartId = cart?.id || (typeof window !== "undefined" ? localStorage.getItem("medusa_cart_id") : null);

  const [formData, setFormData] = useState({
    email: "", firstName: "", lastName: "", phone: "",
    address: "", colonia: "", city: "", state: "", zip: "", country: "México",
  });

  const [step, setStep] = useState(1); // 1: info, 2: pago
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState(null);

  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);

  // Estado del retorno tras redirección de Stripe (OXXO / 3DS).
  const [verifying, setVerifying] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // --- Totales para el resumen (envío según la opción elegida) -------------
  const shippingCost = useMemo(() => {
    if (selectedShipping) return shippingAmount(selectedShipping);
    return 0;
  }, [selectedShipping]);
  const displayTotal = (subtotal || 0) - (discount || 0) + shippingCost;

  // Distribuidoras: el backend rechaza el pedido bajo el mínimo, pero eso pasa
  // al completar — DESPUÉS de autorizar la tarjeta (queda dinero retenido sin
  // pedido). Se bloquea aquí, antes de pedir la tarjeta.
  const distProgress = useDistributorProgress(subtotal);
  const distMinMessage = distProgress.checkoutBlocked
    ? `El pedido mínimo para distribuidoras es de $${distProgress.minTotal.toLocaleString("es-MX")}. Te faltan $${distProgress.missingToMin.toLocaleString("es-MX")}.`
    : null;

  // --- Paso 1 → Paso 2: guarda dirección, envío e inicia sesión de pago ----
  const handleContinueToPayment = async (e) => {
    e.preventDefault();
    if (distProgress.loading) return;
    if (distMinMessage) {
      setError(distMinMessage);
      return;
    }
    if (!cartId) {
      setError("No se pudo inicializar el carrito con la tienda. Recarga la página e intenta de nuevo.");
      return;
    }
    if (!isStripeConfigured) {
      setError("Stripe no está configurado (falta la llave publicable). Avísale al administrador.");
      return;
    }

    setPreparing(true);
    setError(null);
    try {
      await setCartDetails(cartId, formData);

      const allOptions = await listShippingOptions(cartId);

      if (distProgress.isDistributor) {
        // Distribuidoras: solo el envío calculado por peso desde el carrito.
        const quote = await getShippingQuote(cartId);
        if (quote?.status !== "quoted" || !quote.shipping_data) {
          throw new Error(
            quote?.status === "requested"
              ? "Tu envío aún se está calculando. Te avisamos por correo cuando esté listo."
              : "Primero solicita el cálculo de envío desde tu carrito."
          );
        }
        const quoteOption = allOptions.find(isQuoteOption);
        if (!quoteOption) throw new Error("El envío por peso no está disponible. Escríbenos a distribución.");
        const option = { ...quoteOption, amount: quote.amount };
        setShippingOptions([option]);
        setSelectedShipping(option);
        await addShippingMethod(cartId, option.id, quote.shipping_data);
      } else {
        const options = allOptions.filter((o) => !isQuoteOption(o));
        if (!options.length) {
          throw new Error("No hay métodos de envío disponibles para tu dirección. Configúralos en el admin.");
        }
        setShippingOptions(options);
        const first = options[0];
        setSelectedShipping(first);
        await addShippingMethod(cartId, first.id);
      }

      const { clientSecret: secret } = await initStripePaymentSession(cartId);
      setClientSecret(secret);

      setStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.message || "No se pudo preparar el pago.");
    } finally {
      setPreparing(false);
    }
  };

  // --- Cambiar método de envío en el paso 2 --------------------------------
  const handleSelectShipping = async (option) => {
    if (!cartId || option.id === selectedShipping?.id) return;
    setPreparing(true);
    setError(null);
    try {
      setSelectedShipping(option);
      await addShippingMethod(cartId, option.id);
      // Reinicia la sesión para que el PaymentIntent tome el nuevo total.
      const { clientSecret: secret } = await initStripePaymentSession(cartId);
      setClientSecret(secret);
    } catch (err) {
      setError(err.message || "No se pudo actualizar el envío.");
    } finally {
      setPreparing(false);
    }
  };

  // --- Éxito: crea la orden y navega a la confirmación ---------------------
  const handleOrderSuccess = useCallback(
    (order) => {
      clearCart();
      navigate("/order-confirmation", {
        state: {
          orderData: {
            orderNumber: order?.display_id ? `PHY-${order.display_id}` : order?.id,
            email: order?.email || formData.email,
            firstName: order?.shipping_address?.first_name || formData.firstName,
            estimatedDelivery: "3-5 días hábiles",
          },
        },
      });
    },
    [clearCart, navigate, formData.email, formData.firstName]
  );

  // --- Retorno desde Stripe (OXXO / 3DS): verifica el PI y completa --------
  useEffect(() => {
    const isReturn = searchParams.get("redirect") === "stripe";
    const clientSecretParam = searchParams.get("payment_intent_client_secret");
    if (!isReturn || !clientSecretParam || !stripePromise) return;

    let cancelled = false;
    (async () => {
      setVerifying(true);
      setError(null);
      try {
        const stripe = await stripePromise;
        const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecretParam);
        const returningCartId = cart?.id || localStorage.getItem("medusa_cart_id");

        // Tarjeta con 3DS vuelve en requires_capture (Medusa autoriza y se cobra
        // desde el panel). Para OXXO el PI queda en requires_action (voucher
        // emitido) — la orden se crea como pendiente y el webhook la captura.
        const proceed = ["succeeded", "processing", "requires_capture", "requires_action"].includes(paymentIntent?.status);
        if (!returningCartId || !proceed) {
          throw new Error("El pago no se completó. Intenta de nuevo.");
        }

        const res = await completeCart(returningCartId);
        if (cancelled) return;
        if (res.type === "order") {
          handleOrderSuccess(res.order);
        } else {
          throw new Error(res.error?.message || "No se pudo completar el pedido.");
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "No se pudo verificar el pago.");
          setVerifying(false);
        }
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const elementsOptions = useMemo(
    () =>
      clientSecret
        ? {
            clientSecret,
            appearance: {
              theme: "flat",
              variables: {
                colorPrimary: "#5B7A94",
                colorText: "#1C2B3A",
                fontFamily: "Suisse Intl, Inter, sans-serif",
                borderRadius: "8px",
              },
            },
          }
        : null,
    [clientSecret]
  );

  // --- Pantalla de verificación tras redirección ---------------------------
  if (verifying) {
    return (
      <div className="checkout-root">
        <SEO title="Verificando pago" noindex />
        <div className="checkout-container" style={{ textAlign: "center", padding: "80px 20px" }}>
          <h2 className="checkout-section-title">Verificando tu pago…</h2>
          <p>Un momento, estamos confirmando tu pedido.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-root">
      <SEO title="Pago seguro" description="Finaliza tu compra Physiology Skincare Co." noindex />
      <div className="checkout-container">

        {/* Header */}
        <div className="checkout-header">
          <button className="checkout-back-btn" onClick={() => (step === 1 ? navigate("/cart") : setStep(1))}>
            <ArrowLeftIcon />
            <span>{step === 1 ? "Volver al carrito" : "Volver a información"}</span>
          </button>

          <div className="checkout-steps">
            <span className={`chk-step ${step >= 1 ? "active" : ""}`}>
              <span className="chk-step-num">1</span> Información
            </span>
            <span className="chk-step-line" />
            <span className={`chk-step ${step >= 2 ? "active" : ""}`}>
              <span className="chk-step-num">2</span> Pago
            </span>
          </div>
        </div>

        <div className="checkout-layout">

          {/* Columna izquierda: formulario */}
          <div className="checkout-form-col">

            {/* STEP 1: Información personal */}
            {step === 1 && (
              <form onSubmit={handleContinueToPayment} className="checkout-form">
                <section className="checkout-section">
                  <h2 className="checkout-section-title">Información de contacto</h2>

                  <div className="field-group">
                    <label className="field-label">Correo electrónico</label>
                    <input type="email" name="email" className="field-input" placeholder="tu@correo.com"
                      value={formData.email} onChange={handleChange} required />
                  </div>

                  <div className="field-group">
                    <label className="field-label">Teléfono</label>
                    <input type="tel" name="phone" className="field-input" placeholder="+52 000 000 0000"
                      value={formData.phone} onChange={handleChange} />
                  </div>
                </section>

                <section className="checkout-section">
                  <h2 className="checkout-section-title">Dirección de envío</h2>

                  <div className="field-row">
                    <div className="field-group">
                      <label className="field-label">Nombre</label>
                      <input type="text" name="firstName" className="field-input" placeholder="Juan"
                        value={formData.firstName} onChange={handleChange} required />
                    </div>
                    <div className="field-group">
                      <label className="field-label">Apellido</label>
                      <input type="text" name="lastName" className="field-input" placeholder="García"
                        value={formData.lastName} onChange={handleChange} required />
                    </div>
                  </div>

                  <div className="field-group">
                    <label className="field-label">Dirección</label>
                    <input type="text" name="address" className="field-input" placeholder="Calle y número (exterior / interior)"
                      value={formData.address} onChange={handleChange} required />
                  </div>

                  <div className="field-group">
                    <label className="field-label">Colonia</label>
                    <input type="text" name="colonia" className="field-input" placeholder="Colonia"
                      value={formData.colonia} onChange={handleChange} required />
                  </div>

                  <div className="field-row">
                    <div className="field-group">
                      <label className="field-label">Ciudad</label>
                      <input type="text" name="city" className="field-input" placeholder="Ciudad de México"
                        value={formData.city} onChange={handleChange} required />
                    </div>
                    <div className="field-group">
                      <label className="field-label">Estado</label>
                      <StateSearchInput name="state" value={formData.state} onChange={handleChange} required />
                    </div>
                    <div className="field-group field-group--sm">
                      <label className="field-label">C.P.</label>
                      <input type="text" name="zip" className="field-input" placeholder="00000"
                        value={formData.zip} onChange={handleChange} required />
                    </div>
                  </div>

                  <div className="field-group">
                    <label className="field-label">País</label>
                    <select name="country" className="field-input field-select" value={formData.country} onChange={handleChange}>
                      {/* Por ahora sólo envíos nacionales: los demás países quedan
                          visibles pero bloqueados. Para habilitarlos, quitar `disabled`. */}
                      <option>México</option>
                      <option disabled>Estados Unidos</option>
                      <option disabled>España</option>
                      <option disabled>Argentina</option>
                      <option disabled>Colombia</option>
                    </select>
                  </div>
                </section>

                {distMinMessage && !error && <p className="checkout-error">{distMinMessage}</p>}
                {error && <p className="checkout-error">{error}</p>}

                <button
                  type="submit"
                  className="checkout-continue-btn"
                  disabled={preparing || distProgress.loading || Boolean(distMinMessage)}
                >
                  {preparing ? "Preparando pago…" : "Continuar al pago"}
                </button>
              </form>
            )}

            {/* STEP 2: Envío + Pago */}
            {step === 2 && (
              <div className="checkout-form">
                {/* Método de envío */}
                {shippingOptions.length > 0 && (
                  <section className="checkout-section">
                    <h2 className="checkout-section-title">Método de envío</h2>
                    <div className="payment-methods">
                      {shippingOptions.map((opt) => (
                        <label key={opt.id} className={`payment-method-option ${selectedShipping?.id === opt.id ? "selected" : ""}`}>
                          <input type="radio" name="shippingOption" value={opt.id}
                            checked={selectedShipping?.id === opt.id}
                            onChange={() => handleSelectShipping(opt)} disabled={preparing} />
                          <span>{opt.name} — {shippingAmount(opt) === 0 ? "Gratis" : money(shippingAmount(opt))}</span>
                        </label>
                      ))}
                    </div>
                  </section>
                )}

                {/* Pago con Stripe */}
                {elementsOptions && stripePromise ? (
                  <Elements stripe={stripePromise} options={elementsOptions}>
                    <StripePaymentForm cartId={cartId} total={displayTotal} onSuccess={handleOrderSuccess} />
                  </Elements>
                ) : (
                  <section className="checkout-section">
                    <p className="checkout-error">
                      {error || "No se pudo inicializar el pago. Vuelve a intentarlo."}
                    </p>
                  </section>
                )}

                {error && elementsOptions && <p className="checkout-error">{error}</p>}

                {/* Resumen de envío */}
                <section className="checkout-section checkout-section--flat">
                  <h2 className="checkout-section-title">Envío a</h2>
                  <p className="checkout-address-summary">
                    {formData.firstName} {formData.lastName} — {formData.address}, {formData.colonia}, {formData.city}, {formData.state} {formData.zip}, {formData.country}
                  </p>
                </section>
              </div>
            )}
          </div>

          {/* Columna derecha: resumen del pedido */}
          <div className="checkout-summary-col">
            <div className="checkout-summary-box">
              <h2 className="checkout-summary-title">Tu pedido</h2>

              <div className="checkout-items-list">
                {cartItems.map((item) => (
                  <div key={item.id} className="checkout-item">
                    <div className="checkout-item-img-wrap">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="checkout-item-img" />
                      ) : (
                        <div className="checkout-item-img-placeholder" />
                      )}
                      <span className="checkout-item-qty-badge">{item.quantity}</span>
                    </div>
                    <div className="checkout-item-info">
                      <p className="checkout-item-name">{item.name}</p>
                      {item.size && <p className="checkout-item-size">{item.size}</p>}
                    </div>
                    <span className="checkout-item-price">{money(item.price * item.quantity)}</span>
                  </div>
                ))}
              </div>

              <div className="checkout-summary-divider" />

              <div className="checkout-summary-row">
                <span>Subtotal</span>
                <span>{money(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="checkout-summary-row">
                  <span>{has2x1 ? `Descuento (incluye ${ofertasAplicadas.join(', ').toLowerCase()})` : 'Descuento'}</span>
                  <span>−{money(discount)}</span>
                </div>
              )}
              <div className="checkout-summary-row">
                <span>Envío</span>
                <span className={shippingCost === 0 && step === 2 ? "free-shipping" : ""}>
                  {step === 2 ? (shippingCost === 0 ? "Gratis" : money(shippingCost)) : "Se calcula al continuar"}
                </span>
              </div>

              <div className="checkout-summary-divider" />

              <div className="checkout-summary-row checkout-summary-total">
                <span>Total</span>
                <span>{money(step === 2 ? displayTotal : subtotal - (discount || 0))}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
